// @ts-nocheck
/**
 * CTBL v1 — column-major ring-buffer table blob codec (compact capacity).
 *
 * Schema lives in PropertyDefinition.validation.tableSchema; blobs store data only.
 * Blob length encodes live capacity (0…maxRows); empty blobs are header-only (32 bytes).
 * Cell slot sizes remain fixed; append grows capacity until maxRows, then rolling/destructive.
 */

import type {
  TableColumnDefinition,
  TableColumnType,
  TablePropertySchema,
} from '../../types/category';

export const CTBL_MAGIC = 'CTB1';
export const CTBL_VERSION = 1;
export const CTBL_HEADER_SIZE = 32;

export const TABLE_CODEC_LIMITS = {
  maxColumns: 16,
  maxRows: 500,
  maxStringBytes: 256,
} as const;

const INT32_NULL = (0x80000000 | 0);
const INT64_NULL = -(2n ** 63n);
const TIMESTAMP_NULL = BigInt(-1);

export const TABLE_COLUMN_TYPES = [
  'string',
  'int',
  'int64',
  'double',
  'timestamp',
  'color',
] as const satisfies readonly TableColumnType[];

export function isTableColumnType(type: string): type is TableColumnType {
  return (TABLE_COLUMN_TYPES as readonly string[]).includes(type);
}

export type TableCellValue = string | number | null;

export interface TableRow {
  readonly values: readonly TableCellValue[];
}

export interface TableHeader {
  rowCount: number;
  writeHead: number;
  totalAppends: number;
}

export type TableCodecErrorCode =
  | 'invalid_base64'
  | 'invalid_magic'
  | 'invalid_version'
  | 'invalid_schema'
  | 'corrupt_blob'
  | 'row_out_of_range'
  | 'column_out_of_range'
  | 'capacity_full'
  | 'value_too_long'
  | 'invalid_value';

export interface TableCodecResult<T> {
  ok: true;
  value: T;
}

export interface TableCodecError {
  ok: false;
  code: TableCodecErrorCode;
  message: string;
}

export type TableCodecOutcome<T> = TableCodecResult<T> | TableCodecError;

export function isTableCodecError<T>(
  outcome: TableCodecOutcome<T>,
): outcome is TableCodecError {
  return !outcome.ok;
}

function slotSizeForColumn(col: TableColumnDefinition): number {
  switch (col.type) {
    case 'int':
      return 4;
    case 'int64':
    case 'double':
    case 'timestamp':
      return 8;
    case 'string':
    case 'color': {
      const maxBytes = col.maxStringBytes ?? TABLE_CODEC_LIMITS.maxStringBytes;
      return maxBytes;
    }
    default:
      return 0;
  }
}

export function validateTableSchema(schema: TablePropertySchema): string | null {
  if (!schema.columns.length) {
    return 'Table schema requires at least one column.';
  }
  if (schema.columns.length > TABLE_CODEC_LIMITS.maxColumns) {
    return `Table schema exceeds max columns (${TABLE_CODEC_LIMITS.maxColumns}).`;
  }
  if (
    typeof schema.maxRows !== 'number' ||
    schema.maxRows < 1 ||
    schema.maxRows > TABLE_CODEC_LIMITS.maxRows
  ) {
    return `maxRows must be between 1 and ${TABLE_CODEC_LIMITS.maxRows}.`;
  }
  const ids = new Set<string>();
  for (const col of schema.columns) {
    if (!col.id) {
      return 'Each table column requires a non-empty id.';
    }
    if (ids.has(col.id)) {
      return `Duplicate table column id '${col.id}'.`;
    }
    ids.add(col.id);
    if (!isTableColumnType(col.type)) {
      return `Column '${col.id}' has unsupported type '${String(col.type)}'.`;
    }
    if (col.type === 'string' || col.type === 'color') {
      const maxBytes = col.maxStringBytes ?? TABLE_CODEC_LIMITS.maxStringBytes;
      if (maxBytes < 1 || maxBytes > TABLE_CODEC_LIMITS.maxStringBytes) {
        return `Column '${col.id}' maxStringBytes must be between 1 and ${TABLE_CODEC_LIMITS.maxStringBytes}.`;
      }
    }
  }
  if (schema.overflow !== 'rolling' && schema.overflow !== 'destructive') {
    return "Table overflow must be 'rolling' or 'destructive'.";
  }
  return null;
}

/** Bytes per logical row across all column planes. */
export function rowStride(schema: TablePropertySchema): number {
  let stride = 0;
  for (const col of schema.columns) {
    stride += slotSizeForColumn(col);
  }
  return stride;
}

export function computeBlobByteLengthForCapacity(
  schema: TablePropertySchema,
  capacity: number,
): number {
  if (capacity <= 0) {
    return CTBL_HEADER_SIZE;
  }
  return CTBL_HEADER_SIZE + rowStride(schema) * capacity;
}

/** Maximum blob size when capacity === maxRows (estimate / policy helpers). */
export function computeBlobByteLength(schema: TablePropertySchema): number {
  return computeBlobByteLengthForCapacity(schema, schema.maxRows ?? 0);
}

export function capacityFromByteLength(
  schema: TablePropertySchema,
  byteLength: number,
): number | null {
  if (byteLength < CTBL_HEADER_SIZE) {
    return null;
  }
  if (byteLength === CTBL_HEADER_SIZE) {
    return 0;
  }
  const stride = rowStride(schema);
  if (stride <= 0) {
    return null;
  }
  const dataBytes = byteLength - CTBL_HEADER_SIZE;
  if (dataBytes % stride !== 0) {
    return null;
  }
  return dataBytes / stride;
}

function base64ToBytes(base64: string): Uint8Array | null {
  try {
    if (typeof atob === 'function') {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    }
    // Node fallback
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const buf = Buffer.from(base64, 'base64');
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    return btoa(binary);
  }
  // Node fallback
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return Buffer.from(bytes).toString('base64');
}

function readHeader(bytes: Uint8Array): TableHeader | null {
  if (bytes.length < CTBL_HEADER_SIZE) {
    return null;
  }
  const magic = String.fromCharCode(bytes[0]!, bytes[1]!, bytes[2]!, bytes[3]!);
  if (magic !== CTBL_MAGIC) {
    return null;
  }
  if (bytes[4] !== CTBL_VERSION) {
    return null;
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    rowCount: view.getUint16(6, true),
    writeHead: view.getUint16(8, true),
    totalAppends: view.getUint32(10, true),
  };
}

function writeHeader(view: DataView, header: TableHeader): void {
  view.setUint8(0, CTBL_MAGIC.charCodeAt(0));
  view.setUint8(1, CTBL_MAGIC.charCodeAt(1));
  view.setUint8(2, CTBL_MAGIC.charCodeAt(2));
  view.setUint8(3, CTBL_MAGIC.charCodeAt(3));
  view.setUint8(4, CTBL_VERSION);
  view.setUint8(5, 0);
  view.setUint16(6, header.rowCount, true);
  view.setUint16(8, header.writeHead, true);
  view.setUint32(10, header.totalAppends, true);
}

function columnDataOffset(
  schema: TablePropertySchema,
  columnIndex: number,
  capacity: number,
): number {
  let offset = CTBL_HEADER_SIZE;
  for (let i = 0; i < columnIndex; i++) {
    offset += slotSizeForColumn(schema.columns[i]!) * capacity;
  }
  return offset;
}

function physicalSlot(
  header: TableHeader,
  logicalRow: number,
  maxRows: number,
): number {
  if (logicalRow < 0 || logicalRow >= header.rowCount) {
    return -1;
  }
  if (header.rowCount < maxRows) {
    return logicalRow;
  }
  return (header.writeHead - header.rowCount + logicalRow + maxRows) % maxRows;
}

/** Inclusive range that can be stored in an int column. -2147483648 is the null sentinel. */
export const TABLE_INT32_MIN = -2147483647;
export const TABLE_INT32_MAX = 2147483647;

function writeInt32Slot(view: DataView, offset: number, value: number | null): boolean {
  if (value === null) {
    view.setInt32(offset, INT32_NULL, true);
    return true;
  }
  const trunc = Math.trunc(value);
  if (trunc < TABLE_INT32_MIN || trunc > TABLE_INT32_MAX) {
    return false;
  }
  view.setInt32(offset, trunc, true);
  return true;
}

function readInt32Slot(view: DataView, offset: number): number | null {
  const v = view.getInt32(offset, true);
  return v === INT32_NULL ? null : v;
}

function writeDoubleSlot(view: DataView, offset: number, value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    view.setFloat64(offset, Number.NaN, true);
  } else {
    view.setFloat64(offset, value, true);
  }
}

function readDoubleSlot(view: DataView, offset: number): number | null {
  const v = view.getFloat64(offset, true);
  return Number.isNaN(v) ? null : v;
}

function writeInt64Slot(view: DataView, offset: number, value: number | null): boolean {
  if (value === null) {
    view.setBigInt64(offset, INT64_NULL, true);
    return true;
  }
  const trunc = Math.trunc(value);
  if (!Number.isSafeInteger(trunc)) {
    return false;
  }
  view.setBigInt64(offset, BigInt(trunc), true);
  return true;
}

function readInt64Slot(view: DataView, offset: number): number | null {
  const v = view.getBigInt64(offset, true);
  if (v === INT64_NULL) {
    return null;
  }
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : null;
}

function writeTimestampSlot(view: DataView, offset: number, value: number | null): void {
  const big = value === null ? TIMESTAMP_NULL : BigInt(Math.trunc(value));
  view.setBigInt64(offset, big, true);
}

function readTimestampSlot(view: DataView, offset: number): number | null {
  const v = view.getBigInt64(offset, true);
  if (v === TIMESTAMP_NULL) {
    return null;
  }
  return Number(v);
}

function writeStringSlot(bytes: Uint8Array, offset: number, slotSize: number, value: string | null): boolean {
  bytes.fill(0, offset, offset + slotSize);
  if (value === null || value === '') {
    bytes[offset] = 0x00;
    return true;
  }
  const encoded = new TextEncoder().encode(value);
  if (encoded.length >= slotSize) {
    return false;
  }
  bytes[offset] = 0x01;
  bytes.set(encoded, offset + 1);
  return true;
}

function readStringSlot(bytes: Uint8Array, offset: number, slotSize: number): string | null {
  if (bytes[offset] === 0x00) {
    return null;
  }
  const slice = bytes.subarray(offset + 1, offset + slotSize);
  const end = slice.indexOf(0);
  const data = end === -1 ? slice : slice.subarray(0, end);
  if (data.length === 0) {
    return '';
  }
  return new TextDecoder().decode(data);
}

function writeCellToSlot(
  bytes: Uint8Array,
  view: DataView,
  col: TableColumnDefinition,
  offset: number,
  value: TableCellValue,
): boolean {
  switch (col.type) {
    case 'int':
      if (value !== null && typeof value !== 'number') {
        return false;
      }
      return writeInt32Slot(view, offset, value === null ? null : value);
    case 'int64':
      if (value !== null && typeof value !== 'number') {
        return false;
      }
      return writeInt64Slot(view, offset, value === null ? null : value);
    case 'double':
      if (value !== null && typeof value !== 'number') {
        return false;
      }
      writeDoubleSlot(view, offset, value === null ? null : value);
      return true;
    case 'timestamp':
      if (value !== null && typeof value !== 'number') {
        return false;
      }
      writeTimestampSlot(view, offset, value === null ? null : value);
      return true;
    case 'string':
    case 'color':
      if (value !== null && typeof value !== 'string') {
        return false;
      }
      return writeStringSlot(bytes, offset, slotSizeForColumn(col), value);
    default:
      return false;
  }
}

function readCellFromSlot(
  bytes: Uint8Array,
  view: DataView,
  col: TableColumnDefinition,
  offset: number,
): TableCellValue {
  switch (col.type) {
    case 'int':
      return readInt32Slot(view, offset);
    case 'int64':
      return readInt64Slot(view, offset);
    case 'double':
      return readDoubleSlot(view, offset);
    case 'timestamp':
      return readTimestampSlot(view, offset);
    case 'string':
    case 'color':
      return readStringSlot(bytes, offset, slotSizeForColumn(col));
    default:
      return null;
  }
}

/** Item-ref columns (`id`, `*-id`) must use int64 so containd item ids are not truncated. */
export function isItemRefTableColumnId(columnId: string): boolean {
  return columnId === 'id' || columnId.endsWith('-id');
}

/** Promote item-ref `int` columns to `int64`. Does not rewrite stored blobs. */
export function widenItemRefIntColumns(schema: TablePropertySchema): TablePropertySchema {
  let changed = false;
  const columns = schema.columns.map((col) => {
    if (col.type === 'int' && isItemRefTableColumnId(col.id)) {
      changed = true;
      return { ...col, type: 'int64' as const };
    }
    return col;
  });
  return changed ? { ...schema, columns } : schema;
}

/**
 * Inverse of widen for decode fallback: item-ref `int64` columns as `int`.
 * Existing CTBL blobs written before the int64 change use 4-byte slots.
 */
export function itemRefIntFallbackSchema(
  schema: TablePropertySchema,
): TablePropertySchema | null {
  let changed = false;
  const columns = schema.columns.map((col) => {
    if (col.type === 'int64' && isItemRefTableColumnId(col.id)) {
      changed = true;
      return { ...col, type: 'int' as const };
    }
    return col;
  });
  return changed ? { ...schema, columns } : null;
}

function withItemRefIntFallback<T>(
  schema: TablePropertySchema,
  decode: (schema: TablePropertySchema) => TableCodecOutcome<T>,
): TableCodecOutcome<T> {
  const primary = decode(schema);
  if (primary.ok) {
    return primary;
  }
  const fallback = itemRefIntFallbackSchema(schema);
  if (!fallback) {
    return primary;
  }
  const secondary = decode(fallback);
  return secondary.ok ? secondary : primary;
}

/** Decode header, retrying item-ref columns as int when the blob is still int32. */
export function decodeHeaderAllowingItemRefInt(
  base64: string,
  schema: TablePropertySchema,
): TableCodecOutcome<TableHeader> {
  return withItemRefIntFallback(schema, (candidate) => decodeHeader(base64, candidate));
}

/** Decode rows, retrying item-ref columns as int when the blob is still int32. */
export function decodeAllRowsAllowingItemRefInt(
  base64: string,
  schema: TablePropertySchema,
): TableCodecOutcome<TableRow[]> {
  return withItemRefIntFallback(schema, (candidate) => decodeAllRows(base64, candidate));
}

/** Read one row, retrying item-ref columns as int when the blob is still int32. */
export function getRowAllowingItemRefInt(
  base64: string,
  schema: TablePropertySchema,
  logicalRow: number,
): TableCodecOutcome<TableRow> {
  return withItemRefIntFallback(schema, (candidate) => getRow(base64, candidate, logicalRow));
}

/** Read one column, retrying item-ref columns as int when the blob is still int32. */
export function getColumnValuesAllowingItemRefInt(
  base64: string,
  schema: TablePropertySchema,
  columnIndex: number,
): TableCodecOutcome<TableCellValue[]> {
  return withItemRefIntFallback(schema, (candidate) => getColumnValues(base64, candidate, columnIndex));
}

/** Rebuild a blob from decoded rows using a different (same-arity) schema. */
export function migrateTableBlobToSchema(
  base64: string,
  fromSchema: TablePropertySchema,
  toSchema: TablePropertySchema,
): TableCodecOutcome<string> {
  const rows = decodeAllRows(base64, fromSchema);
  if (!rows.ok) {
    return rows;
  }
  const empty = emptyBlobBase64(toSchema);
  if (!empty.ok) {
    return empty;
  }
  let blob = empty.value;
  for (const row of rows.value) {
    const appended = appendRow(blob, toSchema, row.values);
    if (!appended.ok) {
      return appended;
    }
    blob = appended.value;
  }
  return { ok: true, value: blob };
}

/**
 * If an item-ref column was widened to int64 but the stored blob is still int32,
 * rewrite it to the widened schema. Empty / already-matching blobs are unchanged.
 */
export function upgradeItemRefIntBlobIfNeeded(
  base64: string,
  schema: TablePropertySchema,
): TableCodecOutcome<string> {
  if (!base64.trim()) {
    return emptyBlobBase64(schema);
  }
  const asDesired = decodeHeader(base64, schema);
  if (asDesired.ok) {
    return { ok: true, value: base64 };
  }
  const fallback = itemRefIntFallbackSchema(schema);
  if (!fallback) {
    return asDesired;
  }
  const asFallback = decodeHeader(base64, fallback);
  if (!asFallback.ok) {
    return asDesired;
  }
  return migrateTableBlobToSchema(base64, fallback, schema);
}

function reallocateBlob(
  oldBytes: Uint8Array,
  schema: TablePropertySchema,
  oldCapacity: number,
  newCapacity: number,
  header: TableHeader,
): Uint8Array {
  const newBytes = new Uint8Array(computeBlobByteLengthForCapacity(schema, newCapacity));
  const newView = new DataView(newBytes.buffer);
  writeHeader(newView, header);
  if (oldCapacity <= 0 || newCapacity <= 0) {
    return newBytes;
  }
  const copySlots = Math.min(oldCapacity, newCapacity);
  for (let c = 0; c < schema.columns.length; c++) {
    const col = schema.columns[c]!;
    const slotSize = slotSizeForColumn(col);
    const oldOff = columnDataOffset(schema, c, oldCapacity);
    const newOff = columnDataOffset(schema, c, newCapacity);
    newBytes.set(oldBytes.subarray(oldOff, oldOff + copySlots * slotSize), newOff);
  }
  return newBytes;
}

function loadBlob(
  base64: string,
  schema: TablePropertySchema,
): TableCodecOutcome<{
  bytes: Uint8Array;
  view: DataView;
  header: TableHeader;
  capacity: number;
}> {
  const schemaError = validateTableSchema(schema);
  if (schemaError) {
    return { ok: false, code: 'invalid_schema', message: schemaError };
  }
  const bytes = base64ToBytes(base64);
  if (!bytes) {
    return { ok: false, code: 'invalid_base64', message: 'Invalid base64 table blob.' };
  }
  const maxRows = schema.maxRows!;
  const capacity = capacityFromByteLength(schema, bytes.length);
  if (capacity === null || capacity > maxRows) {
    return {
      ok: false,
      code: 'corrupt_blob',
      message: `Invalid CTBL blob length ${bytes.length} for schema.`,
    };
  }
  const header = readHeader(bytes);
  if (!header) {
    const magic = String.fromCharCode(bytes[0]!, bytes[1]!, bytes[2]!, bytes[3]!);
    if (magic !== CTBL_MAGIC) {
      return { ok: false, code: 'invalid_magic', message: 'Invalid CTBL header.' };
    }
    if (bytes[4] !== CTBL_VERSION) {
      return { ok: false, code: 'invalid_version', message: 'Unsupported CTBL version.' };
    }
    return { ok: false, code: 'invalid_magic', message: 'Invalid CTBL header.' };
  }
  if (header.rowCount > capacity || header.rowCount > maxRows) {
    return { ok: false, code: 'corrupt_blob', message: 'rowCount exceeds blob capacity.' };
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return { ok: true, value: { bytes, view, header, capacity } };
}

export function emptyBlobBase64(schema: TablePropertySchema): TableCodecOutcome<string> {
  const schemaError = validateTableSchema(schema);
  if (schemaError) {
    return { ok: false, code: 'invalid_schema', message: schemaError };
  }
  const bytes = new Uint8Array(CTBL_HEADER_SIZE);
  const view = new DataView(bytes.buffer);
  writeHeader(view, { rowCount: 0, writeHead: 0, totalAppends: 0 });
  return { ok: true, value: bytesToBase64(bytes) };
}

export function decodeHeader(base64: string, schema: TablePropertySchema): TableCodecOutcome<TableHeader> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  return { ok: true, value: loaded.value.header };
}

export function decodeAllRows(base64: string, schema: TablePropertySchema): TableCodecOutcome<TableRow[]> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  const { bytes, view, header, capacity } = loaded.value;
  const maxRows = schema.maxRows!;
  const rows: TableRow[] = [];
  for (let r = 0; r < header.rowCount; r++) {
    const values: TableCellValue[] = [];
    for (let c = 0; c < schema.columns.length; c++) {
      const col = schema.columns[c]!;
      const slot = physicalSlot(header, r, maxRows);
      const offset = columnDataOffset(schema, c, capacity) + slot * slotSizeForColumn(col);
      values.push(readCellFromSlot(bytes, view, col, offset));
    }
    rows.push({ values });
  }
  return { ok: true, value: rows };
}

export function getRow(base64: string, schema: TablePropertySchema, logicalRow: number): TableCodecOutcome<TableRow> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  const { bytes, view, header, capacity } = loaded.value;
  const maxRows = schema.maxRows!;
  if (logicalRow < 0 || logicalRow >= header.rowCount) {
    return { ok: false, code: 'row_out_of_range', message: 'Row index out of range.' };
  }
  const slot = physicalSlot(header, logicalRow, maxRows);
  const values: TableCellValue[] = [];
  for (let c = 0; c < schema.columns.length; c++) {
    const col = schema.columns[c]!;
    const offset = columnDataOffset(schema, c, capacity) + slot * slotSizeForColumn(col);
    values.push(readCellFromSlot(bytes, view, col, offset));
  }
  return { ok: true, value: { values } };
}

export function setCell(
  base64: string,
  schema: TablePropertySchema,
  logicalRow: number,
  columnIndex: number,
  value: TableCellValue,
): TableCodecOutcome<string> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  const { bytes, view, header, capacity } = loaded.value;
  const maxRows = schema.maxRows!;
  if (logicalRow < 0 || logicalRow >= header.rowCount) {
    return { ok: false, code: 'row_out_of_range', message: 'Row index out of range.' };
  }
  if (columnIndex < 0 || columnIndex >= schema.columns.length) {
    return { ok: false, code: 'column_out_of_range', message: 'Column index out of range.' };
  }
  const col = schema.columns[columnIndex]!;
  const slot = physicalSlot(header, logicalRow, maxRows);
  const offset = columnDataOffset(schema, columnIndex, capacity) + slot * slotSizeForColumn(col);
  if (!writeCellToSlot(bytes, view, col, offset, value)) {
    return { ok: false, code: 'invalid_value', message: 'Could not encode cell value.' };
  }
  return { ok: true, value: bytesToBase64(bytes) };
}

export function removeRow(
  base64: string,
  schema: TablePropertySchema,
  logicalRow: number,
): TableCodecOutcome<string> {
  const decoded = decodeAllRows(base64, schema);
  if (!decoded.ok) {
    return decoded;
  }
  if (logicalRow < 0 || logicalRow >= decoded.value.length) {
    return { ok: false, code: 'row_out_of_range', message: 'Row index out of range.' };
  }
  const remaining = decoded.value.filter((_, index) => index !== logicalRow);
  const empty = emptyBlobBase64(schema);
  if (!empty.ok) {
    return empty;
  }
  let blob = empty.value;
  for (const row of remaining) {
    const appended = appendRow(blob, schema, row.values);
    if (!appended.ok) {
      return appended;
    }
    blob = appended.value;
  }
  return { ok: true, value: blob };
}

export function appendRow(
  base64: string,
  schema: TablePropertySchema,
  values: readonly TableCellValue[],
): TableCodecOutcome<string> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  if (values.length !== schema.columns.length) {
    return { ok: false, code: 'invalid_value', message: 'Row value count must match column count.' };
  }
  let { bytes, view, header, capacity } = loaded.value;
  const maxRows = schema.maxRows!;
  const overflow = schema.overflow!;

  if (header.rowCount >= maxRows) {
    if (overflow === 'destructive') {
      return { ok: false, code: 'capacity_full', message: 'Table is at capacity (destructive overflow).' };
    }
    // rolling — overwrite at writeHead; capacity already === maxRows
  } else {
    if (header.rowCount >= capacity) {
      const newCapacity = header.rowCount + 1;
      bytes = reallocateBlob(bytes, schema, capacity, newCapacity, header);
      view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      capacity = newCapacity;
    }
    header.writeHead = header.rowCount;
  }

  const slot = header.writeHead;
  for (let c = 0; c < schema.columns.length; c++) {
    const col = schema.columns[c]!;
    const offset = columnDataOffset(schema, c, capacity) + slot * slotSizeForColumn(col);
    if (!writeCellToSlot(bytes, view, col, offset, values[c] ?? null)) {
      return { ok: false, code: 'value_too_long', message: `Value too long for column '${col.id}'.` };
    }
  }

  if (header.rowCount < maxRows) {
    header.rowCount += 1;
    header.writeHead = header.rowCount % maxRows;
  } else {
    header.writeHead = (header.writeHead + 1) % maxRows;
  }
  header.totalAppends += 1;
  writeHeader(view, header);
  return { ok: true, value: bytesToBase64(bytes) };
}

export function getColumnValues(
  base64: string,
  schema: TablePropertySchema,
  columnIndex: number,
): TableCodecOutcome<TableCellValue[]> {
  const loaded = loadBlob(base64, schema);
  if (!loaded.ok) {
    return loaded;
  }
  if (columnIndex < 0 || columnIndex >= schema.columns.length) {
    return { ok: false, code: 'column_out_of_range', message: 'Column index out of range.' };
  }
  const { bytes, view, header, capacity } = loaded.value;
  const maxRows = schema.maxRows!;
  const col = schema.columns[columnIndex]!;
  const result: TableCellValue[] = [];
  for (let r = 0; r < header.rowCount; r++) {
    const slot = physicalSlot(header, r, maxRows);
    const offset = columnDataOffset(schema, columnIndex, capacity) + slot * slotSizeForColumn(col);
    result.push(readCellFromSlot(bytes, view, col, offset));
  }
  return { ok: true, value: result };
}

export function columnTypeLabel(type: TableColumnType): string {
  return type;
}
